import type { ProjectConn } from '../doc/useProject.js';
import { useAwarenessVersion } from '../doc/useY.js';

export interface PeerState {
  clientId: number;
  user?: { id: string; name: string; colour: string };
  pointer?: { boardId: string; x: number; y: number } | null;
  dragging?: { boardId: string; cardIds: string[] } | null;
}

export function usePeers(conn: ProjectConn | null): PeerState[] {
  const awareness = conn?.provider.awareness ?? null;
  useAwarenessVersion(awareness);
  if (!awareness) return [];
  const self = awareness.clientID;
  return [...awareness.getStates().entries()].filter(([id, s]) => id !== self && s.user).map(([clientId, s]) => ({ clientId, ...(s as object) }) as PeerState);
}

/** Other people's pointers on this board, drawn in world coordinates with a constant on-screen size. */
export function Cursors({ peers, boardId, zoom }: { peers: PeerState[]; boardId: string; zoom: number }) {
  return (
    <>
      {peers
        .filter((p) => p.pointer?.boardId === boardId)
        .map((p) => (
          <div key={p.clientId} className="cursor" style={{ transform: `translate(${p.pointer!.x}px, ${p.pointer!.y}px) scale(${1 / zoom})`, color: p.user!.colour }}>
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden>
              <path d="M0 0 L0 13 L4 9 L7 15 L9 14 L6 8 L12 8 z" fill="currentColor" />
            </svg>
            <span style={{ background: p.user!.colour }}>{p.user!.name}</span>
          </div>
        ))}
    </>
  );
}

export function Avatars({ peers, me }: { peers: PeerState[]; me: { name: string; colour: string } }) {
  const seen = new Map<string, { name: string; colour: string }>();
  for (const p of peers) if (p.user) seen.set(p.user.id, p.user);
  return (
    <div className="avatars" aria-label="Online now">
      <span className="avatar" style={{ background: me.colour }} title={`${me.name} (you)`}>
        {me.name.slice(0, 1)}
      </span>
      {[...seen.values()].map((u) => (
        <span key={u.name} className="avatar" style={{ background: u.colour }} title={u.name}>
          {u.name.slice(0, 1)}
        </span>
      ))}
    </div>
  );
}
